import { getApiError } from '../lib/apiError';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, ShoppingCart, Loader2, Trash2, Eye, QrCode, Copy, Check, UserCheck, XCircle, ShieldAlert, Lock, Printer, FileText, Receipt, Search, Package, RefreshCw, Edit3, Filter, X, CheckSquare, DollarSign, CheckCircle, Camera } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { printReceipt, ReceiptPrint } from '../components/ReceiptPrint';
import { BarcodeScannerDialog } from '../components/BarcodeScannerDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Sales = () => {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pixDialogOpen, setPixDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [pixData, setPixData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [editingPayment, setEditingPayment] = useState(false);
  const [cancelForm, setCancelForm] = useState({ password: '', reason: '' });
  const [editPaymentForm, setEditPaymentForm] = useState({ payment_method: '', password: '', reason: '', payment_methods: [] });
  const [cancelNfceDialogOpen, setCancelNfceDialogOpen] = useState(false);
  const [cancelNfceForm, setCancelNfceForm] = useState({ justificativa: '' });
  const [cancellingNfce, setCancellingNfce] = useState(false);
  const [editSellerDialogOpen, setEditSellerDialogOpen] = useState(false);
  const [editSellerForm, setEditSellerForm] = useState({ seller_id: '', password: '', reason: '' });
  const [editingSeller, setEditingSeller] = useState(false);
  const [companyData, setCompanyData] = useState({ name: '', logo: '' });
  const [saleNfce, setSaleNfce] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [showProductList, setShowProductList] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const productSearchRef = useRef(null);
  
  // Estados para edição de venda pendente
  const [editSaleDialogOpen, setEditSaleDialogOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(false);
  const [editSaleForm, setEditSaleForm] = useState({
    client_id: '',
    client_name: '',
    items: [],
    subtotal: 0,
    discount: 0,
    total: 0,
    notes: ''
  });
  const [editProductSearch, setEditProductSearch] = useState('');
  const [showEditProductList, setShowEditProductList] = useState(false);
  const editProductSearchRef = useRef(null);
  
  // Estados dos filtros
  const [filters, setFilters] = useState({
    saleNumber: '',
    paymentMethod: '',
    nfceStatus: '',
    minValue: '',
    maxValue: '',
    startDate: '',
    endDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, per_page: 100, total_count: 0, total_pages: 1 });
  
  // Seleção em massa
  const [selectedSaleIds, setSelectedSaleIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // NFC-e emission
  const [nfceDialogOpen, setNfceDialogOpen] = useState(false);
  const [emittingNfce, setEmittingNfce] = useState(false);
  const [nfceForm, setNfceForm] = useState({ sale_id: '', cpf_consumidor: '', nome_consumidor: '' });
  const [nfceSaleRef, setNfceSaleRef] = useState(null);
  const [nfceSummary, setNfceSummary] = useState({ authorized_total: 0, authorized_count: 0 });

  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    seller_id: '',
    seller_name: '',
    items: [],
    subtotal: 0,
    discount: 0,
    total: 0,
    payment_method: 'dinheiro',
    notes: ''
  });

  const [currentItem, setCurrentItem] = useState({
    product_id: '',
    product_name: '',
    quantity: 1,
    unit_price: 0,
    total: 0
  });

  // Filtrar produtos baseado na busca
  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.sku?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.ean?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.code?.toLowerCase().includes(productSearch.toLowerCase())
  );

  useEffect(() => {
    fetchData();
  }, [currentPage, filters.startDate, filters.endDate]);

  const fetchData = async () => {
    try {
      const salesParams = { page: currentPage, per_page: 100 };
      if (filters.startDate) salesParams.start_date = filters.startDate + 'T00:00:00';
      if (filters.endDate) salesParams.end_date = filters.endDate + 'T23:59:59';
      
      const [salesRes, clientsRes, productsRes, sellersRes, companyRes] = await Promise.all([
        axios.get(`${API_URL}/api/sales`, { params: salesParams }),
        axios.get(`${API_URL}/api/clients`),
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/sellers`),
        axios.get(`${API_URL}/api/company`).catch(() => ({ data: { name: '', logo: '' } }))
      ]);
      setSales(salesRes.data.sales || []);
      setPagination(salesRes.data.pagination || { page: 1, per_page: 100, total_count: 0, total_pages: 1 });
      setNfceSummary(salesRes.data.nfce_summary || { authorized_total: 0, authorized_count: 0 });
      setClients(clientsRes.data);
      setProducts(productsRes.data);
      setSellers(sellersRes.data);
      setCompanyData(companyRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Fechar lista de produtos ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productSearchRef.current && !productSearchRef.current.contains(event.target)) {
        // Verifica se o clique foi dentro da lista de produtos
        const productList = event.target.closest('[data-testid^="product-option-"]');
        if (!productList) {
          setShowProductList(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resetForm = () => {
    setFormData({
      client_id: '', client_name: '', seller_id: '', seller_name: '', items: [], subtotal: 0, discount: 0, total: 0, payment_method: 'dinheiro', notes: ''
    });
    setCurrentItem({ product_id: '', product_name: '', quantity: 1, unit_price: 0, total: 0 });
    setProductSearch('');
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setFormData({ ...formData, client_id: clientId, client_name: client?.name || '' });
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      const total = product.price * currentItem.quantity;
      setCurrentItem({
        ...currentItem,
        product_id: productId,
        product_name: product.name,
        unit_price: product.price,
        total: total
      });
    }
  };

  const handleQuantityChange = (qty) => {
    const quantity = parseInt(qty) || 1;
    setCurrentItem({
      ...currentItem,
      quantity,
      total: currentItem.unit_price * quantity
    });
  };

  // Função para selecionar produto da lista filtrada
  const selectProduct = (product) => {
    setCurrentItem({
      product_id: product.id,
      product_name: product.name,
      quantity: 1,
      unit_price: product.price,
      total: product.price
    });
    setProductSearch(product.name);
    setShowProductList(false);
  };

  // Adiciona item diretamente a partir de um produto (usado pelo leitor de código de barras)
  const addProductDirectly = (product) => {
    const newItem = {
      product_id: product.id,
      product_name: product.name,
      quantity: 1,
      unit_price: product.price,
      total: product.price
    };
    setFormData(prev => {
      const newItems = [...prev.items, newItem];
      const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
      return {
        ...prev,
        items: newItems,
        subtotal,
        total: subtotal - prev.discount
      };
    });
    setCurrentItem({ product_id: '', product_name: '', quantity: 1, unit_price: 0, total: 0 });
    setProductSearch('');
    setShowProductList(false);
    toast.success(`${product.name} adicionado`);
  };

  // Handler do scanner de câmera (celular): adiciona produto direto pelo código lido
  const handleCameraScan = (code) => {
    const scanned = String(code).trim().toLowerCase();
    const match = products.find(p =>
      [p.ean, p.sku, p.code, p.barcode].some(v => v && String(v).toLowerCase() === scanned)
    );
    if (match) {
      addProductDirectly(match);
    } else {
      toast.error(`Produto não encontrado: ${scanned}`);
    }
  };

  // Handler de Enter no campo de busca de produto (leitor de código de barras)
  const handleProductSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Impede o submit do form
      e.stopPropagation();
      
      // Se já tem um produto selecionado no currentItem, adiciona ele
      if (currentItem.product_id) {
        addItem();
        return;
      }
      
      // Senão, busca nos produtos filtrados
      const searchTerm = productSearch.trim();
      if (!searchTerm) return;
      
      // Busca exata por EAN/código de barras primeiro
      const exactMatch = products.find(p => 
        p.ean === searchTerm || p.sku === searchTerm || p.code === searchTerm
      );
      
      if (exactMatch) {
        addProductDirectly(exactMatch);
        return;
      }
      
      // Se tem apenas um resultado na lista filtrada, usa ele
      if (filteredProducts.length === 1) {
        addProductDirectly(filteredProducts[0]);
        return;
      }
      
      // Se tem mais de um resultado, apenas avisa
      if (filteredProducts.length > 1) {
        toast.info(`${filteredProducts.length} produtos encontrados. Selecione um da lista.`);
      } else {
        toast.error('Nenhum produto encontrado');
      }
    }
  };

  const addItem = () => {
    if (!currentItem.product_id || currentItem.quantity < 1) {
      toast.error('Selecione um produto e quantidade');
      return;
    }
    const newItems = [...formData.items, currentItem];
    const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
    setFormData({
      ...formData,
      items: newItems,
      subtotal,
      total: subtotal - formData.discount
    });
    setCurrentItem({ product_id: '', product_name: '', quantity: 1, unit_price: 0, total: 0 });
    setProductSearch('');
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    const subtotal = newItems.reduce((sum, item) => sum + item.total, 0);
    setFormData({
      ...formData,
      items: newItems,
      subtotal,
      total: subtotal - formData.discount
    });
  };

  const handleDiscountChange = (discount) => {
    const discountValue = discount === '' ? 0 : (parseFloat(discount) || 0);
    setFormData({
      ...formData,
      discount: discountValue,
      total: formData.subtotal - discountValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id || formData.items.length === 0) {
      toast.error('Selecione um cliente e adicione itens');
      return;
    }
    
    if (!formData.seller_id) {
      toast.error('Selecione um vendedor');
      return;
    }

    setSaving(true);
    try {
      // Cria a venda como pendente (pagamento será feito no caixa)
      const saleData = {
        ...formData,
        payment_method: 'pendente',  // Sem forma de pagamento definida
        payment_status: 'pendente'   // Status pendente até dar baixa no caixa
      };
      
      await axios.post(`${API_URL}/api/sales`, saleData);
      toast.success('Venda registrada! Finalize o pagamento no caixa.');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao registrar venda'));
    } finally {
      setSaving(false);
    }
  };

  const confirmPixPayment = async () => {
    try {
      await axios.post(`${API_URL}/api/pix/${pixData.id}/confirm`);
      toast.success('Pagamento PIX confirmado!');
      setPixDialogOpen(false);
      setPixData(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao confirmar pagamento');
    }
  };

  const copyPixCode = async () => {
    try {
      await navigator.clipboard.writeText(pixData.br_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Código PIX copiado!');
    } catch (error) {
      toast.error('Erro ao copiar código');
    }
  };

  const viewSale = (sale) => {
    setSelectedSale(sale);
    setViewDialogOpen(true);
  };

  const openCancelDialog = (sale) => {
    setSelectedSale(sale);
    setCancelForm({ password: '', reason: '' });
    setCancelDialogOpen(true);
  };

  const openCancelNfceDialog = (sale) => {
    setSelectedSale(sale);
    setCancelNfceForm({ justificativa: '' });
    setCancelNfceDialogOpen(true);
  };

  const openEditSellerDialog = (sale) => {
    setSelectedSale(sale);
    setEditSellerForm({ seller_id: sale.seller_id || '', password: '', reason: '' });
    setEditSellerDialogOpen(true);
  };

  const handleEditSeller = async () => {
    if (!editSellerForm.seller_id) {
      toast.error('Selecione o vendedor correto');
      return;
    }
    if (!editSellerForm.password) {
      toast.error('Digite a senha de autorização');
      return;
    }
    if (editSellerForm.seller_id === selectedSale?.seller_id) {
      toast.error('Selecione um vendedor diferente do atual');
      return;
    }
    setEditingSeller(true);
    try {
      const res = await axios.put(`${API_URL}/api/pdv/sales/${selectedSale.id}/seller`, {
        seller_id: editSellerForm.seller_id,
        admin_password: editSellerForm.password,
        reason: editSellerForm.reason || 'Correção de vendedor'
      });
      toast.success(res.data.message || 'Vendedor alterado com sucesso!');
      setEditSellerDialogOpen(false);
      setSelectedSale(null);
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao trocar vendedor'));
    } finally {
      setEditingSeller(false);
    }
  };

  const handleCancelNfce = async () => {
    const just = cancelNfceForm.justificativa.trim();
    if (just.length < 15) {
      toast.error('A justificativa deve ter no mínimo 15 caracteres');
      return;
    }
    setCancellingNfce(true);
    try {
      const res = await axios.post(`${API_URL}/api/nfce/${selectedSale.nfce_id}/cancelar`, {
        justificativa: just
      });
      toast.success(res.data.message || 'NFC-e cancelada com sucesso!');
      setCancelNfceDialogOpen(false);
      setSelectedSale(null);
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao cancelar NFC-e'));
    } finally {
      setCancellingNfce(false);
    }
  };

  const openEditPaymentDialog = (sale) => {
    setSelectedSale(sale);
    setEditPaymentForm({ 
      payment_method: sale.payment_method || 'dinheiro', 
      password: '', 
      reason: '',
      payment_methods: sale.payment_method === 'multiplo' && sale.payment_methods?.length > 0
        ? sale.payment_methods.map(pm => ({ method: pm.method, amount: String(pm.amount) }))
        : [{ method: 'dinheiro', amount: '' }, { method: 'pix', amount: '' }]
    });
    setEditPaymentDialogOpen(true);
  };

  const updateEditPaymentRow = (index, field, value) => {
    setEditPaymentForm(prev => {
      const rows = [...prev.payment_methods];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, payment_methods: rows };
    });
  };

  const addEditPaymentRow = () => {
    setEditPaymentForm(prev => ({
      ...prev,
      payment_methods: [...prev.payment_methods, { method: 'dinheiro', amount: '' }]
    }));
  };

  const removeEditPaymentRow = (index) => {
    setEditPaymentForm(prev => ({
      ...prev,
      payment_methods: prev.payment_methods.filter((_, i) => i !== index)
    }));
  };

  const editPaymentSum = editPaymentForm.payment_methods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);

  const handleEditPayment = async () => {
    if (!editPaymentForm.password) {
      toast.error('Digite a senha de autorização');
      return;
    }

    let paymentMethodsPayload = null;
    if (editPaymentForm.payment_method === 'multiplo') {
      const validRows = editPaymentForm.payment_methods.filter(pm => parseFloat(pm.amount) > 0);
      if (validRows.length < 2) {
        toast.error('Pagamento múltiplo exige pelo menos 2 formas com valores');
        return;
      }
      if (Math.abs(editPaymentSum - (selectedSale?.total || 0)) > 0.01) {
        toast.error(`A soma dos valores (${formatCurrency(editPaymentSum)}) deve ser igual ao total da venda (${formatCurrency(selectedSale?.total || 0)})`);
        return;
      }
      paymentMethodsPayload = validRows.map(pm => ({ method: pm.method, amount: parseFloat(pm.amount) }));
    }

    setEditingPayment(true);
    try {
      await axios.put(`${API_URL}/api/pdv/sales/${selectedSale.id}/payment`, {
        payment_method: editPaymentForm.payment_method,
        payment_methods: paymentMethodsPayload,
        admin_password: editPaymentForm.password,
        reason: editPaymentForm.reason || 'Correção de forma de pagamento'
      });
      
      toast.success(`Forma de pagamento atualizada com sucesso!`);
      setEditPaymentDialogOpen(false);
      setSelectedSale(null);
      setEditPaymentForm({ payment_method: '', password: '', reason: '', payment_methods: [] });
      fetchData();
    } catch (error) {
      const errorMsg = getApiError(error, 'Erro ao editar forma de pagamento');
      toast.error(errorMsg);
    } finally {
      setEditingPayment(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await fetchData();
    toast.success('Lista atualizada!');
  };

  const handleCancelSale = async () => {
    if (!cancelForm.password) {
      toast.error('Digite a senha de autorização');
      return;
    }

    if (!cancelForm.reason) {
      toast.error('Informe o motivo do cancelamento');
      return;
    }

    setCancelling(true);
    try {
      await axios.post(`${API_URL}/api/sales/${selectedSale.id}/cancel-authorized`, {
        admin_password: cancelForm.password,
        reason: cancelForm.reason
      });
      
      toast.success(`Venda ${selectedSale.sale_number} cancelada com sucesso!`);
      setCancelDialogOpen(false);
      setSelectedSale(null);
      setCancelForm({ password: '', reason: '' });
      fetchData();
    } catch (error) {
      const errorMsg = getApiError(error, 'Erro ao cancelar venda');
      toast.error(errorMsg);
    } finally {
      setCancelling(false);
    }
  };

  // Função para abrir o dialog de impressão
  const openPrintDialog = async (sale) => {
    setSelectedSale(sale);
    setSaleNfce(null);
    
    // Verifica se existe NFC-e emitida para essa venda
    try {
      const response = await axios.get(`${API_URL}/api/nfce/sale/${sale.id}`);
      if (response.data && response.data.status === 'autorizada') {
        setSaleNfce(response.data);
      }
    } catch (error) {
      // Não há NFC-e para essa venda, apenas cupom não fiscal disponível
      console.log('No NFC-e found for this sale');
    }
    
    setPrintDialogOpen(true);
  };

  // Função para imprimir cupom não fiscal
  const printNonFiscalReceipt = () => {
    if (!selectedSale) {
      toast.error('Nenhuma venda selecionada');
      return;
    }
    
    // Usa a função centralizada de impressão
    const success = printReceipt(selectedSale, { name: companyData.name, logo: companyData.logo }, true);
    if (!success) {
      toast.error('Permita pop-ups para imprimir');
    }
  };

  // Função para imprimir/baixar DANFE (cupom fiscal)
  const printFiscalReceipt = async () => {
    if (!saleNfce) {
      toast.error('Não há NFC-e emitida para esta venda');
      return;
    }
    
    try {
      // Tenta baixar o DANFE
      const response = await axios.get(`${API_URL}/api/nfce/${saleNfce.id}/danfe`, {
        responseType: 'blob'
      });
      
      // Verifica se a resposta é um PDF válido
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errData = JSON.parse(text);
        toast.error(errData.detail || 'Erro ao gerar DANFE');
        return;
      }
      
      // Cria um link para download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DANFE_${selectedSale.sale_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('DANFE baixado com sucesso!');
    } catch (error) {
      const detail = getApiError(error, 'Erro desconhecido');
      toast.error(`Erro ao baixar DANFE: ${detail}`);
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // ================== FUNÇÕES PARA EDIÇÃO DE VENDA PENDENTE ==================
  
  const openEditSaleDialog = (sale) => {
    setSelectedSale(sale);
    setEditSaleForm({
      client_id: sale.client_id || '',
      client_name: sale.client_name || '',
      items: sale.items.map(item => ({ ...item })),
      subtotal: sale.subtotal,
      discount: sale.discount,
      total: sale.total,
      notes: sale.notes || ''
    });
    setEditSaleDialogOpen(true);
  };

  const addProductToEdit = (product) => {
    const existingIndex = editSaleForm.items.findIndex(item => item.product_id === product.id);
    
    if (existingIndex >= 0) {
      // Incrementa quantidade se já existe
      const newItems = [...editSaleForm.items];
      newItems[existingIndex].quantity += 1;
      newItems[existingIndex].total = newItems[existingIndex].quantity * newItems[existingIndex].unit_price;
      
      const newSubtotal = newItems.reduce((sum, item) => sum + item.total, 0);
      setEditSaleForm({
        ...editSaleForm,
        items: newItems,
        subtotal: newSubtotal,
        total: newSubtotal - editSaleForm.discount
      });
    } else {
      // Adiciona novo item
      const newItem = {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.price,
        total: product.price
      };
      
      const newItems = [...editSaleForm.items, newItem];
      const newSubtotal = newItems.reduce((sum, item) => sum + item.total, 0);
      
      setEditSaleForm({
        ...editSaleForm,
        items: newItems,
        subtotal: newSubtotal,
        total: newSubtotal - editSaleForm.discount
      });
    }
    
    setEditProductSearch('');
    setShowEditProductList(false);
  };

  const updateEditItemQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    
    const newItems = [...editSaleForm.items];
    newItems[index].quantity = newQuantity;
    newItems[index].total = newQuantity * newItems[index].unit_price;
    
    const newSubtotal = newItems.reduce((sum, item) => sum + item.total, 0);
    setEditSaleForm({
      ...editSaleForm,
      items: newItems,
      subtotal: newSubtotal,
      total: newSubtotal - editSaleForm.discount
    });
  };

  const removeEditItem = (index) => {
    const newItems = editSaleForm.items.filter((_, i) => i !== index);
    const newSubtotal = newItems.reduce((sum, item) => sum + item.total, 0);
    
    setEditSaleForm({
      ...editSaleForm,
      items: newItems,
      subtotal: newSubtotal,
      total: newSubtotal - editSaleForm.discount
    });
  };

  const updateEditDiscount = (discountValue) => {
    const discount = discountValue === '' ? 0 : (parseFloat(discountValue) || 0);
    setEditSaleForm({
      ...editSaleForm,
      discount: discount,
      total: editSaleForm.subtotal - discount
    });
  };

  const saveEditedSale = async () => {
    if (editSaleForm.items.length === 0) {
      toast.error('Adicione pelo menos um produto');
      return;
    }

    setEditingSale(true);
    try {
      await axios.put(`${API_URL}/api/sales/${selectedSale.id}`, {
        client_id: editSaleForm.client_id,
        client_name: editSaleForm.client_name,
        items: editSaleForm.items,
        subtotal: editSaleForm.subtotal,
        discount: editSaleForm.discount,
        total: editSaleForm.total,
        notes: editSaleForm.notes,
        payment_method: 'pendente',
        payment_status: 'pendente'
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Venda atualizada com sucesso!');
      setEditSaleDialogOpen(false);
      fetchData(); // Refresh the sales list
    } catch (error) {
      console.error('Erro ao atualizar venda:', error);
      toast.error(getApiError(error, 'Erro ao atualizar venda'));
    } finally {
      setEditingSale(false);
    }
  };

  const filteredEditProducts = products.filter(p => 
    editProductSearch && (
      p.name.toLowerCase().includes(editProductSearch.toLowerCase()) ||
      (p.sku && p.sku.toLowerCase().includes(editProductSearch.toLowerCase())) ||
      (p.code && p.code.toLowerCase().includes(editProductSearch.toLowerCase()))
    )
  );

  // ================== FIM FUNÇÕES DE EDIÇÃO ==================

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Função para filtrar vendas
  // Seleção em massa
  const toggleSelectSale = (id) => {
    setSelectedSaleIds(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]);
  };
  const toggleSelectAllSales = () => {
    if (selectedSaleIds.length === filteredSales.length) {
      setSelectedSaleIds([]);
    } else {
      setSelectedSaleIds(filteredSales.map(s => s.id));
    }
  };
  const handleBulkDelete = async () => {
    if (!window.confirm(`Tem certeza que deseja excluir ${selectedSaleIds.length} venda(s)? Esta ação não pode ser desfeita.`)) return;
    const adminPassword = prompt('Digite a senha de administrador para confirmar:');
    if (!adminPassword) return;
    setBulkDeleting(true);
    try {
      const response = await axios.delete(`${API_URL}/api/sales/bulk-delete`, {
        data: { sale_ids: selectedSaleIds, admin_password: adminPassword }
      });
      toast.success(response.data.message);
      setSelectedSaleIds([]);
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao excluir vendas'));
    } finally {
      setBulkDeleting(false);
    }
  };

  const filteredSales = sales.filter(sale => {
    // Filtro por número da venda
    if (filters.saleNumber && !sale.sale_number.toLowerCase().includes(filters.saleNumber.toLowerCase())) {
      return false;
    }
    
    // Filtro por forma de pagamento
    if (filters.paymentMethod && sale.payment_method !== filters.paymentMethod) {
      return false;
    }
    
    // Filtro por status NFC-e
    if (filters.nfceStatus) {
      if (filters.nfceStatus === 'sem_nfce') {
        if (sale.nfce_status) return false;
      } else {
        if (sale.nfce_status !== filters.nfceStatus) return false;
      }
    }
    
    // Filtro por valor mínimo
    if (filters.minValue && sale.total < parseFloat(filters.minValue)) {
      return false;
    }
    
    // Filtro por valor máximo
    if (filters.maxValue && sale.total > parseFloat(filters.maxValue)) {
      return false;
    }
    
    return true;
  });

  // Função para limpar filtros
  const clearFilters = () => {
    setFilters({
      saleNumber: '',
      paymentMethod: '',
      nfceStatus: '',
      minValue: '',
      maxValue: '',
      startDate: '',
      endDate: ''
    });
  };

  // Verifica se algum filtro está ativo
  const hasActiveFilters = filters.saleNumber || filters.paymentMethod || filters.nfceStatus || filters.minValue || filters.maxValue || filters.startDate || filters.endDate;

  const paymentStatusColors = {
    pendente: 'bg-amber-100 text-amber-800',
    pago: 'bg-emerald-100 text-emerald-800',
    cancelado: 'bg-red-100 text-red-800'
  };

  const paymentStatusLabels = {
    pendente: 'Pendente',
    pago: 'Finalizado',
    cancelado: 'Cancelado'
  };

  const paymentMethodLabels = {
    dinheiro: 'Dinheiro',
    pix: 'PIX',
    pix_cnpj: 'PIX - CNPJ',
    cartao_credito: 'Cartão Crédito',
    cartao_debito: 'Cartão Débito',
    multiplo: 'Múltiplo',
    crediario_bemol: 'Crediário Bemol',
    vale_mercadoria: 'Vale Mercadoria',
    avancard: 'Avancard',
    boleto: 'Boleto'
  };

  const nfceStatusColors = {
    autorizada: 'bg-emerald-100 text-emerald-800',
    pendente: 'bg-blue-100 text-blue-800',
    rejeitada: 'bg-red-100 text-red-800'
  };

  const nfceStatusLabels = {
    autorizada: 'Autorizada',
    pendente: 'Pendente',
    rejeitada: 'Rejeitada'
  };

  const openNfceDialog = (sale) => {
    setNfceSaleRef(sale);
    setNfceForm({ sale_id: sale.id, cpf_consumidor: '', nome_consumidor: '' });
    setNfceDialogOpen(true);
  };

  const handleEmitNfce = async () => {
    setEmittingNfce(true);
    try {
      const payload = { sale_id: nfceForm.sale_id };
      if (nfceForm.cpf_consumidor) payload.cpf_consumidor = nfceForm.cpf_consumidor.replace(/\D/g, '');
      if (nfceForm.nome_consumidor) payload.nome_consumidor = nfceForm.nome_consumidor;
      const response = await axios.post(`${API_URL}/api/nfce/emit`, payload);
      if (response.data.success) {
        toast.success(response.data.message || 'NFC-e autorizada com sucesso!');
      } else {
        toast.error(response.data.message || 'NFC-e rejeitada pela SEFAZ');
      }
      setNfceDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao emitir NFC-e'));
    } finally {
      setEmittingNfce(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="sales-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vendas</h1>
          <p className="text-slate-500 mt-1">Registre e acompanhe suas vendas</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className={hasActiveFilters ? 'border-blue-500 text-blue-600' : ''}
            data-testid="toggle-filters-btn"
          >
            <Filter size={18} className="mr-2" />
            Filtros
            {hasActiveFilters && (
              <Badge className="ml-2 bg-blue-600 text-white text-xs px-1.5">
                {[filters.saleNumber, filters.paymentMethod, filters.minValue, filters.maxValue].filter(Boolean).length}
              </Badge>
            )}
          </Button>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={loading}
            data-testid="refresh-sales-btn"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-sale-btn">
                <Plus size={18} className="mr-2" />
                Nova Venda
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nova Venda</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Cliente */}
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select value={formData.client_id} onValueChange={handleClientChange}>
                  <SelectTrigger data-testid="sale-client">
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vendedor */}
              <div className="space-y-2">
                <Label>Vendedor *</Label>
                <Select 
                  value={formData.seller_id} 
                  onValueChange={(v) => {
                    const seller = sellers.find(s => s.id === v);
                    setFormData({ ...formData, seller_id: v, seller_name: seller?.name || '' });
                  }}
                >
                  <SelectTrigger data-testid="sale-seller">
                    <SelectValue placeholder="Selecione o vendedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {sellers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex items-center gap-2">
                          <UserCheck size={14} />
                          {s.name} {s.commission > 0 && `(${s.commission}%)`}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Adicionar Item */}
              <Card className="border border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Package size={16} />
                    Adicionar Produto
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Campo de busca de produto */}
                    <div className="md:col-span-2 relative">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                          <Input
                            ref={productSearchRef}
                            type="text"
                            value={productSearch}
                            onChange={(e) => {
                              setProductSearch(e.target.value);
                              setShowProductList(true);
                              // Limpa o produto selecionado se o usuário começar a digitar algo diferente
                              if (currentItem.product_name && e.target.value !== currentItem.product_name) {
                                setCurrentItem({ product_id: '', product_name: '', quantity: 1, unit_price: 0, total: 0 });
                              }
                            }}
                            onFocus={() => setShowProductList(true)}
                            onKeyDown={handleProductSearchKeyDown}
                            placeholder="Buscar produto por nome, SKU ou código de barras..."
                            className="pl-10"
                            data-testid="sale-product-search"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="shrink-0 border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                          onClick={() => setScannerOpen(true)}
                          title="Escanear com a câmera"
                          data-testid="open-camera-scanner-btn"
                        >
                          <Camera size={18} />
                        </Button>
                      </div>
                      
                      {/* Lista de produtos filtrados */}
                      {showProductList && productSearch && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                          {filteredProducts.length > 0 ? (
                            filteredProducts.slice(0, 20).map((product) => (
                              <div
                                key={product.id}
                                className={`p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 ${
                                  currentItem.product_id === product.id ? 'bg-blue-100' : ''
                                }`}
                                onClick={() => selectProduct(product)}
                                data-testid={`product-option-${product.id}`}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-medium text-slate-800">{product.name}</p>
                                    <p className="text-xs text-slate-500">
                                      {product.sku && `SKU: ${product.sku} • `}
                                      Estoque: {product.stock} un
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-emerald-600">{formatCurrency(product.price)}</p>
                                    <Badge variant="outline" className="text-xs">
                                      {product.stock > 10 ? 'Em estoque' : product.stock > 0 ? 'Baixo estoque' : 'Sem estoque'}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-4 text-center text-slate-500">
                              <Package size={24} className="mx-auto mb-2 text-slate-300" />
                              <p>Nenhum produto encontrado</p>
                              <p className="text-xs">Tente outro termo de busca</p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Produto selecionado */}
                      {currentItem.product_id && (
                        <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Check size={16} className="text-emerald-600" />
                            <span className="text-sm font-medium text-emerald-800">{currentItem.product_name}</span>
                          </div>
                          <span className="text-sm font-bold text-emerald-600">{formatCurrency(currentItem.unit_price)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Quantidade */}
                    <div>
                      <Input
                        type="number"
                        min="1"
                        value={currentItem.quantity}
                        onChange={(e) => handleQuantityChange(e.target.value)}
                        placeholder="Qtd"
                        data-testid="sale-quantity"
                        disabled={!currentItem.product_id}
                      />
                      {currentItem.product_id && currentItem.quantity > 0 && (
                        <p className="text-xs text-slate-500 mt-1 text-center">
                          Total: {formatCurrency(currentItem.total)}
                        </p>
                      )}
                    </div>
                    
                    {/* Botão Adicionar */}
                    <div>
                      <Button 
                        type="button" 
                        onClick={addItem} 
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        disabled={!currentItem.product_id}
                        data-testid="sale-add-item"
                      >
                        <Plus size={16} className="mr-1" />
                        Adicionar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Itens da Venda */}
              {formData.items.length > 0 && (
                <Card className="border border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Itens da Venda</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Produto</TableHead>
                          <TableHead className="text-center">Qtd</TableHead>
                          <TableHead className="text-right">Unitário</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.product_name}</TableCell>
                            <TableCell className="text-center">{item.quantity}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(item.total)}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => removeItem(index)} className="text-red-600">
                                <Trash2 size={16} />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Totais */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Subtotal</Label>
                  <Input value={formatCurrency(formData.subtotal)} readOnly className="bg-slate-50" />
                </div>
                <div className="space-y-2">
                  <Label>Desconto</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.discount || ''}
                    onChange={(e) => handleDiscountChange(e.target.value)}
                    placeholder="0,00"
                    data-testid="sale-discount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total</Label>
                  <Input value={formatCurrency(formData.total)} readOnly className="bg-slate-50 font-bold text-lg" />
                </div>
              </div>

              {/* Observações */}
              <div className="space-y-2">
                <Label>Observações</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Observações da venda"
                  data-testid="sale-notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-sale-btn">
                  {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                  Registrar Venda
                </Button>
              </div>

              {/* Aviso sobre pagamento */}
              <p className="text-sm text-slate-500 text-center">
                * O pagamento será realizado no caixa (PDV)
              </p>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Painel de Filtros */}
      {showFilters && (
        <Card className="border border-slate-200 bg-slate-50" data-testid="filters-panel">
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Filtro por Data Início */}
              <div className="space-y-2">
                <Label htmlFor="filter-start-date" className="text-sm font-medium">Data Início</Label>
                <Input
                  id="filter-start-date"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => { setFilters({ ...filters, startDate: e.target.value }); setCurrentPage(1); }}
                  className="bg-white"
                  data-testid="filter-start-date"
                />
              </div>

              {/* Filtro por Data Fim */}
              <div className="space-y-2">
                <Label htmlFor="filter-end-date" className="text-sm font-medium">Data Fim</Label>
                <Input
                  id="filter-end-date"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => { setFilters({ ...filters, endDate: e.target.value }); setCurrentPage(1); }}
                  className="bg-white"
                  data-testid="filter-end-date"
                />
              </div>

              {/* Filtro por Número da Venda */}
              <div className="space-y-2">
                <Label htmlFor="filter-sale-number" className="text-sm font-medium">Número da Venda</Label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="filter-sale-number"
                    type="text"
                    placeholder="Ex: PDV-000001"
                    value={filters.saleNumber}
                    onChange={(e) => setFilters({ ...filters, saleNumber: e.target.value })}
                    className="pl-10 bg-white"
                    data-testid="filter-sale-number"
                  />
                </div>
              </div>

              {/* Filtro por Forma de Pagamento */}
              <div className="space-y-2">
                <Label htmlFor="filter-payment" className="text-sm font-medium">Forma de Pagamento</Label>
                <Select 
                  value={filters.paymentMethod} 
                  onValueChange={(value) => setFilters({ ...filters, paymentMethod: value === 'todos' ? '' : value })}
                >
                  <SelectTrigger className="bg-white" data-testid="filter-payment-method">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todas</SelectItem>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="pix_cnpj">PIX - CNPJ</SelectItem>
                    <SelectItem value="cartao_credito">Cartão Crédito</SelectItem>
                    <SelectItem value="cartao_debito">Cartão Débito</SelectItem>
                    <SelectItem value="crediario_bemol">Crediário Bemol</SelectItem>
                    <SelectItem value="vale_mercadoria">Vale Mercadoria</SelectItem>
                    <SelectItem value="avancard">Avancard</SelectItem>
                    <SelectItem value="boleto">Boleto</SelectItem>
                    <SelectItem value="multiplo">Múltiplo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Status NFC-e */}
              <div className="space-y-2">
                <Label htmlFor="filter-nfce" className="text-sm font-medium">Status NFC-e</Label>
                <Select 
                  value={filters.nfceStatus} 
                  onValueChange={(value) => setFilters({ ...filters, nfceStatus: value === 'todos' ? '' : value })}
                >
                  <SelectTrigger className="bg-white" data-testid="filter-nfce-status">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="autorizada">Autorizada</SelectItem>
                    <SelectItem value="rejeitada">Rejeitada</SelectItem>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="sem_nfce">Sem NFC-e</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Valor Mínimo */}
              <div className="space-y-2">
                <Label htmlFor="filter-min-value" className="text-sm font-medium">Valor Mínimo (R$)</Label>
                <Input
                  id="filter-min-value"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={filters.minValue}
                  onChange={(e) => setFilters({ ...filters, minValue: e.target.value })}
                  className="bg-white"
                  data-testid="filter-min-value"
                />
              </div>

              {/* Filtro por Valor Máximo */}
              <div className="space-y-2">
                <Label htmlFor="filter-max-value" className="text-sm font-medium">Valor Máximo (R$)</Label>
                <Input
                  id="filter-max-value"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={filters.maxValue}
                  onChange={(e) => setFilters({ ...filters, maxValue: e.target.value })}
                  className="bg-white"
                  data-testid="filter-max-value"
                />
              </div>
            </div>

            {/* Botão Limpar Filtros */}
            {hasActiveFilters && (
              <div className="mt-4 flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearFilters}
                  className="text-slate-600"
                  data-testid="clear-filters-btn"
                >
                  <X size={16} className="mr-2" />
                  Limpar Filtros
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Card de NF Autorizadas - oculto para vendedores */}
      {user?.role !== 'vendedor' && (
      <Card className="border-l-4 border-l-emerald-500 border border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total NFC-e Autorizadas</p>
              <p className="text-2xl font-bold text-emerald-600" data-testid="total-nfce-authorized">
                {formatCurrency(nfceSummary.authorized_total)}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {nfceSummary.authorized_count} nota(s) autorizada(s)
              </p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-lg">
              <CheckCircle className="text-emerald-600" size={24} />
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Selection Bar */}
      {selectedSaleIds.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="sales-selection-bar">
          <span className="text-sm font-medium text-blue-800">
            {selectedSaleIds.length} venda(s) selecionada(s)
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelectedSaleIds([])} className="text-slate-600">
              <X size={14} className="mr-1" /> Limpar
            </Button>
            {isAdmin && (
              <Button size="sm" onClick={handleBulkDelete} className="bg-red-500 hover:bg-red-600 text-white" disabled={bulkDeleting} data-testid="bulk-delete-sales-btn">
                {bulkDeleting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Trash2 size={14} className="mr-1" />}
                Excluir Selecionadas
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <ShoppingCart size={20} className="text-blue-600" />
            Lista de Vendas ({pagination.total_count} total{hasActiveFilters ? ` - ${filteredSales.length} filtradas` : ''})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-blue-600" size={32} />
            </div>
          ) : filteredSales.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ShoppingCart size={48} className="mx-auto mb-4 text-slate-300" />
              {hasActiveFilters ? (
                <>
                  <p>Nenhuma venda encontrada com os filtros aplicados</p>
                  <Button 
                    variant="link" 
                    onClick={clearFilters}
                    className="mt-2 text-blue-600"
                  >
                    Limpar filtros
                  </Button>
                </>
              ) : (
                <p>Nenhuma venda encontrada</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selectedSaleIds.length === filteredSales.length && filteredSales.length > 0}
                        onCheckedChange={toggleSelectAllSales}
                        data-testid="select-all-sales"
                      />
                    </TableHead>
                    <TableHead>Nº Venda</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Pagamento</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead className="text-center">NFC-e</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.map((sale) => (
                    <TableRow key={sale.id} data-testid={`sale-row-${sale.id}`} className={`${sale.payment_status === 'cancelado' ? 'opacity-60' : ''} ${selectedSaleIds.includes(sale.id) ? 'bg-blue-50' : ''}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedSaleIds.includes(sale.id)}
                          onCheckedChange={() => toggleSelectSale(sale.id)}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{sale.sale_number}</TableCell>
                      <TableCell className="font-medium">{sale.client_name}</TableCell>
                      <TableCell>{formatDate(sale.created_at)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(sale.total)}</TableCell>
                      <TableCell>
                        {sale.payment_method === 'multiplo' && sale.payment_methods && sale.payment_methods.length > 0 ? (
                          <div className="group relative">
                            <span className="cursor-help border-b border-dashed border-slate-400">
                              {paymentMethodLabels[sale.payment_method]}
                            </span>
                            <div className="absolute z-50 hidden group-hover:block bg-white border shadow-lg rounded-lg p-2 min-w-[150px] left-0 top-full mt-1">
                              <p className="text-xs text-slate-500 mb-1 font-medium">Detalhamento:</p>
                              {sale.payment_methods.map((pm, idx) => (
                                <div key={idx} className="flex justify-between text-sm py-0.5">
                                  <span className="text-slate-600">{paymentMethodLabels[pm.method] || pm.method}</span>
                                  <span className="font-medium ml-3">{formatCurrency(pm.amount)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          paymentMethodLabels[sale.payment_method] || sale.payment_method
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={paymentStatusColors[sale.payment_status]} title={sale.payment_status === 'pago' ? 'Venda finalizada' : sale.payment_status === 'cancelado' ? `Cancelado por: ${sale.cancelled_by_name || 'Admin'}` : ''}>
                          {paymentStatusLabels[sale.payment_status] || sale.payment_status}
                          {sale.payment_status === 'pago' && <Lock size={12} className="ml-1 inline" />}
                          {sale.payment_status === 'cancelado' && <XCircle size={12} className="ml-1 inline" />}
                        </Badge>
                      </TableCell>
                      <TableCell>{sale.seller_name}</TableCell>
                      <TableCell className="text-center">
                        {sale.nfce_status ? (
                          <Badge className={nfceStatusColors[sale.nfce_status] || 'bg-slate-100 text-slate-800'} data-testid={`nfce-status-${sale.id}`}>
                            {nfceStatusLabels[sale.nfce_status] || sale.nfce_status}
                          </Badge>
                        ) : sale.payment_status === 'pago' ? (
                          <span className="text-xs text-slate-400">-</span>
                        ) : (
                          <span className="text-xs text-slate-300">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => viewSale(sale)} className="text-slate-600 hover:text-blue-600" title="Ver detalhes">
                            <Eye size={16} />
                          </Button>
                          {/* Botão de emitir NFC-e - só para vendas pagas sem NFC-e autorizada */}
                          {sale.payment_status === 'pago' && sale.nfce_status !== 'autorizada' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openNfceDialog(sale)} 
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              title={sale.nfce_status === 'rejeitada' ? 'Reenviar NFC-e para SEFAZ' : 'Emitir NFC-e para SEFAZ'}
                              data-testid={`emit-nfce-${sale.id}`}
                            >
                              <FileText size={16} />
                            </Button>
                          )}
                          {/* Botão de editar venda pendente - só aparece para vendas pendentes */}
                          {sale.payment_status === 'pendente' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openEditSaleDialog(sale)} 
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Editar venda pendente"
                              data-testid={`edit-sale-${sale.id}`}
                            >
                              <Edit3 size={16} />
                            </Button>
                          )}
                          {/* Botão de editar pagamento - só aparece para admin e vendas pagas não canceladas */}
                          {isAdmin && sale.payment_status === 'pago' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openEditPaymentDialog(sale)} 
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Editar forma de pagamento (requer senha)"
                              data-testid={`edit-payment-${sale.id}`}
                            >
                              <Edit3 size={16} />
                            </Button>
                          )}
                          {/* Botão de impressão - só aparece para vendas finalizadas */}
                          {sale.payment_status === 'pago' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openPrintDialog(sale)} 
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Imprimir cupom"
                              data-testid={`print-sale-${sale.id}`}
                            >
                              <Printer size={16} />
                            </Button>
                          )}
                          {/* Botão de trocar vendedor - admin, vendas não canceladas */}
                          {isAdmin && sale.payment_status !== 'cancelado' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openEditSellerDialog(sale)} 
                              className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                              title="Trocar vendedor (requer senha)"
                              data-testid={`edit-seller-${sale.id}`}
                            >
                              <UserCheck size={16} />
                            </Button>
                          )}
                          {/* Botão de cancelar NFC-e - só para admin em NFC-e autorizada */}
                          {isAdmin && sale.nfce_status === 'autorizada' && sale.nfce_id && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openCancelNfceDialog(sale)} 
                              className="text-purple-500 hover:text-purple-700 hover:bg-purple-50"
                              title="Cancelar NFC-e emitida (evento SEFAZ)"
                              data-testid={`cancel-nfce-${sale.id}`}
                            >
                              <ShieldAlert size={16} />
                            </Button>
                          )}
                          {/* Botão de cancelamento - só aparece para admin e vendas não canceladas */}
                          {isAdmin && sale.payment_status !== 'cancelado' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openCancelDialog(sale)} 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Cancelar venda (requer senha)"
                              data-testid={`cancel-sale-${sale.id}`}
                            >
                              <XCircle size={16} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Paginação */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t" data-testid="sales-pagination">
              <p className="text-sm text-slate-500">
                Página {pagination.page} de {pagination.total_pages} ({pagination.total_count} vendas)
              </p>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  Primeira
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                >
                  Anterior
                </Button>
                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                  let pageNum;
                  if (pagination.total_pages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.total_pages - 2) {
                    pageNum = pagination.total_pages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? 'default' : 'outline'}
                      size="sm"
                      className={currentPage === pageNum ? 'bg-blue-600' : ''}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === pagination.total_pages}
                  onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}
                >
                  Próxima
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === pagination.total_pages}
                  onClick={() => setCurrentPage(pagination.total_pages)}
                >
                  Última
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog open={editPaymentDialogOpen} onOpenChange={setEditPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Edit3 size={24} />
              Editar Forma de Pagamento
            </DialogTitle>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-4">
              {/* Info */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-medium">⚠️ Atenção!</p>
                <p className="text-sm text-amber-700 mt-1">
                  Você está editando a forma de pagamento da venda <strong>{selectedSale.sale_number}</strong> no valor de <strong>{formatCurrency(selectedSale.total)}</strong>.
                </p>
                <p className="text-sm text-amber-700 mt-2">
                  Esta alteração será registrada para auditoria.
                </p>
              </div>

              {/* Pagamento Atual */}
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-500">Pagamento atual:</p>
                <p className="font-medium">{paymentMethodLabels[selectedSale.payment_method] || selectedSale.payment_method}</p>
              </div>

              {/* Nova forma de pagamento */}
              <div className="space-y-2">
                <Label>Nova Forma de Pagamento</Label>
                <Select 
                  value={editPaymentForm.payment_method} 
                  onValueChange={(v) => setEditPaymentForm({ ...editPaymentForm, payment_method: v })}
                >
                  <SelectTrigger data-testid="edit-payment-method">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="pix_cnpj">PIX - CNPJ</SelectItem>
                    <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                    <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                    <SelectItem value="multiplo">Múltiplo</SelectItem>
                    <SelectItem value="crediario_bemol">Crediário Bemol</SelectItem>
                    <SelectItem value="vale_mercadoria">Vale Mercadoria</SelectItem>
                    <SelectItem value="avancard">Avancard</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Valores do pagamento múltiplo */}
              {editPaymentForm.payment_method === 'multiplo' && (
                <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="edit-payment-multiplo-section">
                  <div className="flex items-center justify-between">
                    <Label className="text-blue-800">Formas e Valores</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs border-blue-300 text-blue-700"
                      onClick={addEditPaymentRow}
                      data-testid="add-payment-row-btn"
                    >
                      <Plus size={12} className="mr-1" /> Adicionar
                    </Button>
                  </div>
                  {editPaymentForm.payment_methods.map((pm, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Select
                        value={pm.method}
                        onValueChange={(v) => updateEditPaymentRow(idx, 'method', v)}
                      >
                        <SelectTrigger className="flex-1 bg-white" data-testid={`payment-row-method-${idx}`}>
                          <SelectValue placeholder="Forma" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dinheiro">Dinheiro</SelectItem>
                          <SelectItem value="pix">PIX</SelectItem>
                          <SelectItem value="pix_cnpj">PIX - CNPJ</SelectItem>
                          <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                          <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                          <SelectItem value="crediario_bemol">Crediário Bemol</SelectItem>
                          <SelectItem value="vale_mercadoria">Vale Mercadoria</SelectItem>
                          <SelectItem value="avancard">Avancard</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={pm.amount}
                        onChange={(e) => updateEditPaymentRow(idx, 'amount', e.target.value)}
                        placeholder="0,00"
                        className="w-28 bg-white"
                        data-testid={`payment-row-amount-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 shrink-0"
                        onClick={() => removeEditPaymentRow(idx)}
                        disabled={editPaymentForm.payment_methods.length <= 2}
                        data-testid={`remove-payment-row-${idx}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                  <div className={`flex justify-between text-sm font-medium pt-2 border-t border-blue-200 ${Math.abs(editPaymentSum - (selectedSale?.total || 0)) <= 0.01 ? 'text-emerald-700' : 'text-red-600'}`}>
                    <span>Soma: {formatCurrency(editPaymentSum)}</span>
                    <span data-testid="edit-payment-sum-status">
                      {Math.abs(editPaymentSum - (selectedSale?.total || 0)) <= 0.01
                        ? '✓ Confere com o total'
                        : `Faltam ${formatCurrency((selectedSale?.total || 0) - editPaymentSum)}`}
                    </span>
                  </div>
                </div>
              )}

              {/* Motivo */}
              <div className="space-y-2">
                <Label>Motivo da Alteração (opcional)</Label>
                <Input
                  value={editPaymentForm.reason}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, reason: e.target.value })}
                  placeholder="Ex: Lançamento incorreto no caixa"
                  data-testid="edit-payment-reason"
                />
              </div>

              {/* Senha de autorização */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Lock size={16} className="text-amber-600" />
                  Senha do Administrador
                </Label>
                <Input
                  type="password"
                  value={editPaymentForm.password}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, password: e.target.value })}
                  placeholder="Digite sua senha para autorizar"
                  data-testid="edit-payment-password"
                />
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setEditPaymentDialogOpen(false)}
                  disabled={editingPayment}
                >
                  Cancelar
                </Button>
                <Button 
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={handleEditPayment}
                  disabled={editingPayment || !editPaymentForm.password || !editPaymentForm.payment_method}
                  data-testid="confirm-edit-payment-btn"
                >
                  {editingPayment ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Check size={18} className="mr-2" />
                      Salvar Alteração
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Sale Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldAlert size={24} />
              Cancelar Venda
            </DialogTitle>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-4">
              {/* Warning */}
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">⚠️ Atenção!</p>
                <p className="text-sm text-red-700 mt-1">
                  Você está prestes a cancelar a venda <strong>{selectedSale.sale_number}</strong> no valor de <strong>{formatCurrency(selectedSale.total)}</strong>.
                </p>
                <p className="text-sm text-red-700 mt-2">
                  Esta ação irá estornar o estoque dos produtos e não pode ser desfeita.
                </p>
              </div>

              {/* Sale Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Cliente:</span>
                    <p className="font-medium">{selectedSale.client_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Data:</span>
                    <p className="font-medium">{formatDate(selectedSale.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Vendedor:</span>
                    <p className="font-medium">{selectedSale.seller_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Pagamento:</span>
                    <p className="font-medium">{paymentMethodLabels[selectedSale.payment_method]}</p>
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label htmlFor="cancel-reason">Motivo do Cancelamento *</Label>
                <Textarea
                  id="cancel-reason"
                  value={cancelForm.reason}
                  onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })}
                  placeholder="Informe o motivo do cancelamento..."
                  rows={2}
                  data-testid="cancel-reason"
                />
              </div>

              {/* Admin Password */}
              <div className="space-y-2">
                <Label htmlFor="admin-password" className="flex items-center gap-1">
                  <Lock size={14} />
                  Senha de Autorização (Admin) *
                </Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={cancelForm.password}
                  onChange={(e) => setCancelForm({ ...cancelForm, password: e.target.value })}
                  placeholder="Digite sua senha para autorizar"
                  data-testid="cancel-password"
                />
                <p className="text-xs text-slate-500">
                  Apenas administradores podem cancelar vendas finalizadas.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setCancelDialogOpen(false)}
                  disabled={cancelling}
                >
                  Voltar
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleCancelSale}
                  disabled={cancelling || !cancelForm.password || !cancelForm.reason}
                  data-testid="confirm-cancel-btn"
                >
                  {cancelling ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      Cancelando...
                    </>
                  ) : (
                    <>
                      <XCircle size={18} className="mr-2" />
                      Confirmar Cancelamento
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Dialog - Escolha de tipo de cupom */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <Printer size={24} />
              Imprimir Cupom
            </DialogTitle>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-4">
              {/* Sale Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Venda:</span>
                    <p className="font-medium">{selectedSale.sale_number}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Data:</span>
                    <p className="font-medium">{formatDate(selectedSale.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Cliente:</span>
                    <p className="font-medium">{selectedSale.client_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Total:</span>
                    <p className="font-medium text-emerald-600">{formatCurrency(selectedSale.total)}</p>
                  </div>
                </div>
              </div>

              {/* Print Options */}
              <div className="space-y-3">
                <p className="text-sm text-slate-600 font-medium">Escolha o tipo de documento:</p>
                
                {/* Cupom Não Fiscal */}
                <div 
                  className="p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-all"
                  onClick={printNonFiscalReceipt}
                  data-testid="print-non-fiscal"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Receipt size={24} className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">Cupom Não Fiscal</p>
                      <p className="text-sm text-slate-500">Comprovante simples de venda (2ª via)</p>
                    </div>
                    <Printer size={20} className="text-slate-400" />
                  </div>
                </div>

                {/* Cupom Fiscal (NFC-e / DANFE) */}
                <div 
                  className={`p-4 border rounded-lg transition-all ${
                    saleNfce 
                      ? 'hover:border-emerald-500 hover:bg-emerald-50 cursor-pointer' 
                      : 'opacity-50 cursor-not-allowed bg-slate-50'
                  }`}
                  onClick={saleNfce ? printFiscalReceipt : undefined}
                  data-testid="print-fiscal"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${saleNfce ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                      <FileText size={24} className={saleNfce ? 'text-emerald-600' : 'text-slate-400'} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${saleNfce ? 'text-slate-800' : 'text-slate-500'}`}>
                        Cupom Fiscal (DANFE NFC-e)
                      </p>
                      <p className="text-sm text-slate-500">
                        {saleNfce 
                          ? `NFC-e autorizada: ${saleNfce.chave_acesso?.substring(0, 20)}...`
                          : 'Não há NFC-e emitida para esta venda'
                        }
                      </p>
                    </div>
                    {saleNfce ? (
                      <Badge className="bg-emerald-100 text-emerald-800">Disponível</Badge>
                    ) : (
                      <Badge className="bg-slate-200 text-slate-500">Indisponível</Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Hidden Receipt for Print */}
              <div className="sr-only" aria-hidden="true">
                <ReceiptPrint sale={selectedSale} companyName={companyData.name} companyLogo={companyData.logo} isReprint={true} />
              </div>

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PIX Dialog */}
      <Dialog open={pixDialogOpen} onOpenChange={setPixDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="text-blue-600" />
              Pagamento PIX
            </DialogTitle>
          </DialogHeader>
          {pixData && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <img 
                  src={pixData.qr_code_base64} 
                  alt="QR Code PIX" 
                  className="w-64 h-64 border rounded-lg"
                  data-testid="pix-qrcode"
                />
              </div>
              
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{formatCurrency(pixData.amount)}</p>
                <p className="text-slate-500 mt-1">{pixData.description}</p>
              </div>

              <div className="space-y-2">
                <Label>Código PIX (Copia e Cola)</Label>
                <div className="flex gap-2">
                  <Input 
                    value={pixData.br_code} 
                    readOnly 
                    className="text-xs font-mono"
                    data-testid="pix-code"
                  />
                  <Button 
                    variant="outline" 
                    onClick={copyPixCode}
                    className="shrink-0"
                    data-testid="copy-pix-btn"
                  >
                    {copied ? <Check size={18} className="text-emerald-600" /> : <Copy size={18} />}
                  </Button>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setPixDialogOpen(false)}>
                  Fechar
                </Button>
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={confirmPixPayment}
                  data-testid="confirm-pix-btn"
                >
                  Confirmar Pagamento
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Sale Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes da Venda {selectedSale?.sale_number}</DialogTitle>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Cliente</Label>
                  <p className="font-medium">{selectedSale.client_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Vendedor</Label>
                  <p className="font-medium">{selectedSale.seller_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Data</Label>
                  <p className="font-medium">{formatDate(selectedSale.created_at)}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Pagamento</Label>
                  <div className="flex items-center gap-2">
                    <span>{paymentMethodLabels[selectedSale.payment_method]}</span>
                    <Badge className={paymentStatusColors[selectedSale.payment_status]}>
                      {selectedSale.payment_status}
                    </Badge>
                  </div>
                  {/* Detalhamento do Pagamento Múltiplo */}
                  {selectedSale.payment_method === 'multiplo' && selectedSale.payment_methods && selectedSale.payment_methods.length > 0 && (
                    <div className="mt-2 p-2 bg-slate-50 rounded-lg border">
                      <p className="text-xs text-slate-500 mb-1">Detalhamento:</p>
                      <div className="space-y-1">
                        {selectedSale.payment_methods.map((pm, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-slate-600">{paymentMethodLabels[pm.method] || pm.method}</span>
                            <span className="font-medium">{formatCurrency(pm.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead className="text-center">Qtd</TableHead>
                    <TableHead className="text-right">Unitário</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedSale.items.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell className="text-center">{item.quantity}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-end gap-8 pt-4 border-t">
                <div className="text-right">
                  <p className="text-slate-500 text-sm">Subtotal</p>
                  <p className="font-medium">{formatCurrency(selectedSale.subtotal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500 text-sm">Desconto</p>
                  <p className="font-medium">{formatCurrency(selectedSale.discount)}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500 text-sm">Total</p>
                  <p className="text-xl font-bold text-blue-600">{formatCurrency(selectedSale.total)}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Pending Sale Dialog */}
      <Dialog open={editSaleDialogOpen} onOpenChange={setEditSaleDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Edit3 size={24} />
              Editar Venda Pendente - {selectedSale?.sale_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Info da venda */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Você pode adicionar ou remover produtos desta venda pendente. Após salvar, a venda continuará pendente até ser paga no PDV.
              </p>
            </div>

            {/* Cliente e Observações */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select 
                  value={editSaleForm.client_id} 
                  onValueChange={(value) => {
                    const client = clients.find(c => c.id === value);
                    setEditSaleForm({
                      ...editSaleForm,
                      client_id: value,
                      client_name: client?.name || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Observações</Label>
                <Input 
                  value={editSaleForm.notes}
                  onChange={(e) => setEditSaleForm({ ...editSaleForm, notes: e.target.value })}
                  placeholder="Observações..."
                />
              </div>
            </div>

            {/* Busca de produtos */}
            <div className="space-y-2">
              <Label>Adicionar Produto</Label>
              <div className="relative" ref={editProductSearchRef}>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Buscar produto por nome ou código..."
                    value={editProductSearch}
                    onChange={(e) => {
                      setEditProductSearch(e.target.value);
                      setShowEditProductList(e.target.value.length > 0);
                    }}
                    className="pl-10"
                    data-testid="edit-product-search"
                  />
                </div>
                {showEditProductList && filteredEditProducts.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {filteredEditProducts.slice(0, 10).map((product) => (
                      <div
                        key={product.id}
                        className="px-4 py-2 hover:bg-slate-100 cursor-pointer flex justify-between items-center"
                        onClick={() => addProductToEdit(product)}
                      >
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-xs text-slate-500">{product.sku || product.code}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">{formatCurrency(product.price)}</p>
                          <p className="text-xs text-slate-500">Est: {product.stock}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Lista de itens */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Produto</TableHead>
                    <TableHead className="text-center w-32">Quantidade</TableHead>
                    <TableHead className="text-right">Unitário</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editSaleForm.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        <Package size={32} className="mx-auto mb-2 text-slate-300" />
                        Nenhum produto adicionado
                      </TableCell>
                    </TableRow>
                  ) : (
                    editSaleForm.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.product_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-8 w-8 p-0"
                              onClick={() => updateEditItemQuantity(index, item.quantity - 1)}
                            >
                              -
                            </Button>
                            <span className="w-12 text-center font-medium">{item.quantity}</span>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-8 w-8 p-0"
                              onClick={() => updateEditItemQuantity(index, item.quantity + 1)}
                            >
                              +
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(item.total)}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeEditItem(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Totais e Desconto */}
            <div className="flex justify-between items-end p-4 bg-slate-50 rounded-lg">
              <div className="space-y-2">
                <Label>Desconto (R$)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={editSaleForm.discount || ''}
                  onChange={(e) => updateEditDiscount(e.target.value)}
                  className="w-32"
                  data-testid="edit-discount"
                />
              </div>
              <div className="text-right space-y-1">
                <p className="text-sm text-slate-500">Subtotal: {formatCurrency(editSaleForm.subtotal)}</p>
                <p className="text-sm text-slate-500">Desconto: -{formatCurrency(editSaleForm.discount)}</p>
                <p className="text-2xl font-bold text-blue-600">Total: {formatCurrency(editSaleForm.total)}</p>
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={() => setEditSaleDialogOpen(false)}
                disabled={editingSale}
              >
                Cancelar
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={saveEditedSale}
                disabled={editingSale || editSaleForm.items.length === 0}
                data-testid="save-edited-sale-btn"
              >
                {editingSale ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Check size={18} className="mr-2" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Emitir NFC-e */}
      <Dialog open={nfceDialogOpen} onOpenChange={setNfceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} className="text-purple-600" />
              Emitir NFC-e
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-800">
                Venda: <strong>{nfceSaleRef?.sale_number}</strong> - {formatCurrency(nfceSaleRef?.total || 0)}
              </p>
              <p className="text-xs text-purple-600 mt-1">
                O documento fiscal será gerado e enviado para a SEFAZ.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cpf_consumidor">CPF do Consumidor (opcional)</Label>
              <Input
                id="cpf_consumidor"
                value={nfceForm.cpf_consumidor}
                onChange={(e) => setNfceForm({ ...nfceForm, cpf_consumidor: e.target.value })}
                placeholder="000.000.000-00"
                maxLength={14}
                data-testid="nfce-cpf-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nome_consumidor">Nome do Consumidor (opcional)</Label>
              <Input
                id="nome_consumidor"
                value={nfceForm.nome_consumidor}
                onChange={(e) => setNfceForm({ ...nfceForm, nome_consumidor: e.target.value })}
                placeholder="Nome completo"
                data-testid="nfce-nome-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => setNfceDialogOpen(false)}>Cancelar</Button>
              <Button
                onClick={handleEmitNfce}
                disabled={emittingNfce}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="confirm-emit-nfce-btn"
              >
                {emittingNfce ? <Loader2 className="animate-spin mr-2" size={18} /> : <FileText size={18} className="mr-2" />}
                Emitir NFC-e
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scanner de código de barras via câmera (celular) */}
      <BarcodeScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onScan={handleCameraScan}
      />

      {/* Dialog Trocar Vendedor */}
      <Dialog open={editSellerDialogOpen} onOpenChange={setEditSellerDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="edit-seller-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-700">
              <UserCheck size={20} />
              Trocar Vendedor da Venda
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
              <p className="text-sm text-cyan-800">
                Venda <strong>{selectedSale?.sale_number}</strong> — Vendedor atual: <strong>{selectedSale?.seller_name || 'Não informado'}</strong>
              </p>
              <p className="text-xs text-cyan-600 mt-1">A alteração fica registrada para auditoria e reflete nas comissões.</p>
            </div>

            <div className="space-y-2">
              <Label>Novo vendedor <span className="text-red-500">*</span></Label>
              <Select
                value={editSellerForm.seller_id}
                onValueChange={(v) => setEditSellerForm({ ...editSellerForm, seller_id: v })}
              >
                <SelectTrigger data-testid="edit-seller-select">
                  <SelectValue placeholder="Selecione o vendedor correto" />
                </SelectTrigger>
                <SelectContent>
                  {sellers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}{s.id === selectedSale?.seller_id ? ' (atual)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Motivo (opcional)</Label>
              <Input
                value={editSellerForm.reason}
                onChange={(e) => setEditSellerForm({ ...editSellerForm, reason: e.target.value })}
                placeholder="Ex: venda lançada no vendedor errado"
                data-testid="edit-seller-reason"
              />
            </div>

            <div className="space-y-2">
              <Label>Senha de administrador <span className="text-red-500">*</span></Label>
              <Input
                type="password"
                value={editSellerForm.password}
                onChange={(e) => setEditSellerForm({ ...editSellerForm, password: e.target.value })}
                placeholder="Digite sua senha"
                data-testid="edit-seller-password"
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditSellerDialogOpen(false)} disabled={editingSeller}>
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                onClick={handleEditSeller}
                disabled={editingSeller}
                data-testid="confirm-edit-seller-btn"
              >
                {editingSeller ? <Loader2 size={16} className="mr-2 animate-spin" /> : <UserCheck size={16} className="mr-2" />}
                {editingSeller ? 'Salvando...' : 'Trocar Vendedor'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Cancelar NFC-e */}
      <Dialog open={cancelNfceDialogOpen} onOpenChange={setCancelNfceDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="cancel-nfce-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <ShieldAlert size={20} />
              Cancelar NFC-e Emitida
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-1">
              <p className="text-sm font-medium text-red-800">
                ⚠️ Esta ação envia um evento de cancelamento à SEFAZ e não pode ser desfeita.
              </p>
              <p className="text-xs text-red-600">
                Venda: <strong>{selectedSale?.sale_number}</strong> — NFC-e nº {selectedSale?.nfce_numero} — {formatCurrency(selectedSale?.total || 0)}
              </p>
              <p className="text-xs text-red-600">
                A SEFAZ só aceita cancelamento dentro do prazo legal (em geral, até 24h após a emissão).
              </p>
            </div>

            <div className="space-y-2">
              <Label>Justificativa do cancelamento <span className="text-red-500">*</span></Label>
              <Textarea
                value={cancelNfceForm.justificativa}
                onChange={(e) => setCancelNfceForm({ justificativa: e.target.value })}
                placeholder="Ex: Erro na forma de pagamento informada na emissão"
                rows={3}
                data-testid="cancel-nfce-justificativa"
              />
              <p className={`text-xs ${cancelNfceForm.justificativa.trim().length >= 15 ? 'text-emerald-600' : 'text-slate-400'}`}>
                {cancelNfceForm.justificativa.trim().length}/15 caracteres mínimos
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setCancelNfceDialogOpen(false)}
                disabled={cancellingNfce}
              >
                Voltar
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={handleCancelNfce}
                disabled={cancellingNfce || cancelNfceForm.justificativa.trim().length < 15}
                data-testid="confirm-cancel-nfce-btn"
              >
                {cancellingNfce ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ShieldAlert size={16} className="mr-2" />}
                {cancellingNfce ? 'Enviando à SEFAZ...' : 'Cancelar NFC-e'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sales;
